import {
  FC,
  useState,
  useEffect,
  createContext,
  useCallback,
  useContext,
  useRef,
  Dispatch,
  SetStateAction,
} from 'react'
import axios from 'axios'
import { useQueryClient } from 'react-query'
import { useLocation } from 'react-router-dom'
import { ehTermoCienciaPendente } from '../../apps/termos/core/_models'
import { LayoutSplashScreen } from '../../../../_metronic/layout/core'
import { AuthTokenResponse, UsuarioWithImage } from './_models'
import { clearLoginEmpresaId, clearTipoCert, loadLoginEmpresaId } from './AuthHelpers'
import { getAvatarUser, getLoggedUser, logoutUser, refreshToken } from './_requests'
import { clearBrandSplash } from '../../brand-management/core/brandSplash'
import {
  getStoredPushToken,
  setStoredPushToken,
} from '../../apps/notificacoes/push/pushTokenStorage'
import { PlataformaType, showError, WithChildren } from '../../../../_metronic/helpers'
import { getEmpresaSlugFromLoginPath, prefetchLoginCompanyConfig } from './loginCompanyConfig'
import { clearSessionLocked, isSessionLocked } from '../inatividade/sessionLockStorage'
import { ehSessaoExpirada, marcarSessaoExpirada } from './sessaoExpirada'

type AuthContextProps = {
  auth: AuthTokenResponse | undefined
  saveAuth: (auth: AuthTokenResponse | undefined) => void
  currentUser: UsuarioWithImage | undefined
  setCurrentUser: Dispatch<SetStateAction<UsuarioWithImage | undefined>>
  logout: () => Promise<void>
  reloadUser: () => Promise<void>
}

const initAuthContextPropsState = {
  auth: undefined,
  saveAuth: () => {},
  currentUser: undefined,
  setCurrentUser: () => {},
  logout: async () => {},
  reloadUser: async () => {},
}

const AuthContext = createContext<AuthContextProps>(initAuthContextPropsState)

const useAuth = () => useContext(AuthContext)

const AuthProvider: FC<WithChildren> = ({ children }) => {
  const [auth, setAuth] = useState<AuthTokenResponse | undefined>(undefined)
  const [currentUser, setCurrentUser] = useState<UsuarioWithImage | undefined>()

  // Identidades estáveis: quem depende delas em efeitos (AuthInit, InactivityProvider, Logout)
  // não reexecuta por re-render do provider.
  const saveAuth = useCallback((auth: AuthTokenResponse | undefined) => {
    setAuth(auth)
    if (!auth) {
      clearTipoCert()
      clearLoginEmpresaId()
      clearBrandSplash()
    }
  }, [])

  const logout = useCallback(async () => {
    try {
      await logoutUser({
        pushToken: getStoredPushToken() ?? undefined,
      })
    } catch (error: any) {
      if (error?.response && error.response.status !== 403) showError(error)
    } finally {
      clearSessionLocked()
      setStoredPushToken(null)
      saveAuth(undefined)
      setCurrentUser(undefined)
    }
  }, [saveAuth])

  const reloadUser = useCallback(async () => {
    try {
      const usuario = await getLoggedUser()
      if (usuario) {
        let imagemPerfil: string

        try {
          const avatar = await getAvatarUser()
          imagemPerfil = avatar ?? '/media/avatars/AVATARES_05.png'
        } catch {
          imagemPerfil = '/media/avatars/AVATARES_05.png'
        }

        setCurrentUser({ ...usuario, imagemPerfil })
      }
    } catch (error) {
      logout()
    }
  }, [logout])

  return (
    <AuthContext.Provider
      value={{ auth, saveAuth, currentUser, setCurrentUser, logout, reloadUser }}
    >
      {children}
    </AuthContext.Provider>
  )
}
const AuthInit: FC<WithChildren> = ({ children }) => {
  const { auth, saveAuth, logout, setCurrentUser, reloadUser } = useAuth()
  const location = useLocation()
  const queryClient = useQueryClient()
  const didRequest = useRef(false)
  const [showSplashScreen, setShowSplashScreen] = useState(true)

  // LGPD: termo de ciência publicado no meio da sessão (ex.: o próprio admin acabou de publicar
  // uma versão). A partir daí o backend responde 403 TERMO_CIENCIA_PENDENTE em tudo; recarregar
  // o usuário traz a pendência e o AppRoutes desvia para a tela do termo. O ref evita reentrada:
  // o próprio reload chama o avatar, que também leva 403 enquanto a pendência existir.
  const recarregandoPorTermo = useRef(false)
  // Token vencido ou revogado no meio do uso: encerra a sessão local e o roteamento leva ao login,
  // que avisa o motivo. Só depois do bootstrap e com sessão aberta — 401 no bootstrap é só "não
  // logado". O ref segura as várias requisições que falham juntas num único logout.
  const sessaoAberta = useRef(false)
  sessaoAberta.current = !!auth
  const encerrandoSessao = useRef(false)
  useEffect(() => {
    if (auth) encerrandoSessao.current = false
  }, [auth])
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(undefined, async (error) => {
      if (
        ehSessaoExpirada(error) &&
        didRequest.current &&
        sessaoAberta.current &&
        !encerrandoSessao.current
      ) {
        encerrandoSessao.current = true
        marcarSessaoExpirada()
        logout()
      }
      if (ehTermoCienciaPendente(error) && !recarregandoPorTermo.current) {
        recarregandoPorTermo.current = true
        try {
          await reloadUser()
        } finally {
          recarregandoPorTermo.current = false
        }
      }
      return Promise.reject(error)
    })
    return () => axios.interceptors.response.eject(interceptor)
  }, [reloadUser, logout])

  // Bootstrap da sessão: roda UMA vez, na montagem. O que ele lê do momento da montagem (token,
  // rota de login) vai por ref de propósito — pôr `pathname` e `accessToken` no array faria o
  // bootstrap repetir a cada navegação e a cada renovação de token.
  const montagem = useRef({ accessToken: auth?.accessToken, pathname: location.pathname })
  montagem.current = { accessToken: auth?.accessToken, pathname: location.pathname }

  useEffect(() => {
    const { accessToken, pathname } = montagem.current
    const preloadPublicLoginCompanyConfig = () =>
      prefetchLoginCompanyConfig(queryClient, getEmpresaSlugFromLoginPath(pathname))

    const requestUser = async () => {
      const usuario = await getLoggedUser()

      if (usuario) {
        let imagemPerfil: string

        try {
          imagemPerfil = await getAvatarUser()
        } catch (error) {
          imagemPerfil = '/media/avatars/AVATARES_05.png'
        }
        setCurrentUser({ ...usuario, imagemPerfil })
      }
    }

    const init = async () => {
      try {
        if (!didRequest.current) {
          // F5 com a sessão bloqueada por inatividade: não renova o token, exige novo login.
          if (isSessionLocked()) {
            throw new Error('Sessão bloqueada por inatividade')
          }

          if (!accessToken) {
            const refreshed = await refreshToken({
              plataforma: PlataformaType.Site,
              empresa: loadLoginEmpresaId(),
            })
            saveAuth(refreshed)
          }

          await requestUser()
        }
      } catch (error) {
        if (!didRequest.current) {
          logout()
        }
      } finally {
        await preloadPublicLoginCompanyConfig()
        setShowSplashScreen(false)
        didRequest.current = true
      }
    }

    init()
  }, [logout, queryClient, saveAuth, setCurrentUser])

  return showSplashScreen ? <LayoutSplashScreen /> : <>{children}</>
}

export { AuthProvider, AuthInit, useAuth }
